// ============================================================
// CY_BORG Bestiary — NPC & Foe generators (thematic, on-brand)
// Content is fan-written in the CY_BORG voice; swap for verbatim
// rulebook lines any time.
// ============================================================

// ---------- helpers ----------
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rollDie = (sides, count = 1, mod = 0) => {
  let total = mod;
  for (let i = 0; i < count; i++) total += Math.floor(Math.random() * sides) + 1;
  return total;
};
const parseDie = (str) => {
  // supports plain int "8", or "d6", "d8+2", "2d6+1", "d10a"
  const s = String(str).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const m = s.match(/(\d*)d(\d+)([+-]\d+)?/i);
  if (!m) return 0;
  const count = m[1] ? parseInt(m[1], 10) : 1;
  const sides = parseInt(m[2], 10);
  const mod = m[3] ? parseInt(m[3], 10) : 0;
  return rollDie(sides, count, mod);
};

// ============================================================
// NPC GENERATOR
// ============================================================

export const NPC_TYPES = [
  "STREET KID", "CORPO DRONE", "JUNKYARD SCAV", "CULT GHOUL",
  "GUTTER MEDIC", "GLITCH PREACHER", "WIREHEADED EX-COP", "DUST-BROKER",
  "CHROME-INK ARTIST", "ROGUE SYNTH", "BURNT NANOMANCER", "CHIP-DEALER",
];

export const NPC_LOOKS = [
  "hazmat suit two sizes too small",
  "blood-crusted lab coat",
  "cracked mirror faceplate",
  "corp-issued blazer, stained collar",
  "ripped fatigues, home-stitched patches",
  "plastic bodybag poncho",
  "mirror-shard armour glued to skin",
  "wrapped head-to-toe in stimpatches",
  "glowing tattoos beneath translucent skin",
  "half a face; the other half is chrome",
  "gas-mask that never comes off",
  "wire-mesh veil over hollow eyes",
  "wet leather, still dripping",
  "animal-skull crown, real teeth",
  "hex tattoos self-inked in ballpoint",
  "luminescent fungus stitched into the arm",
  "ash smeared across a shaven scalp",
  "shredded fur coat, no shirt beneath",
  "burnt hoodie, mirror-lens goggles",
  "surgical gown tied with piano wire",
];

export const NPC_QUIRKS = [
  "laughs at their own jokes, no one else does",
  "blinks in slow, deliberate binary",
  "hums an anthem no one remembers",
  "chews stimchips like gum, constantly",
  "mutters map coordinates under breath",
  "front tooth replaced with dull gold",
  "eats out of a corpo-brand wrapper. Always the same brand.",
  "has forgotten how to blink",
  "shoulder tic gets worse when lying",
  "one hand shakes; the other is too still",
  "lisps proudly, corrects anyone who mimics them",
  "wears sunglasses even in the sewers",
  "records everything on a jury-rigged deck",
  "whispers to their own palm",
  "sniffs anyone new before speaking",
  "coughs a fine mist of blood every few minutes, politely",
  "spits neon-green — chews some experimental leaf",
  "wears a stopped watch, still checks it",
  "carries a jar with one dead beetle inside",
  "always freezing, even in the heat",
];

export const NPC_MOTIVES = [
  "revenge", "cash", "faith", "addiction",
  "family", "boredom", "escape", "love",
  "fear", "curiosity", "grief", "hunger",
];

export const NPC_WANTS = [
  "looking for a friend they lost last week",
  "selling something wildly illegal",
  "buying information about you",
  "hiding from a corp bounty",
  "waiting quietly to die",
  "spreading a rumour they made up",
  "testing an untested drug on themselves",
  "trying to save a kid they don't know",
  "delivering a message they haven't read",
  "running from something that isn't there",
  "collecting one more soul for the church",
  "recruiting someone stupid enough to try",
];

// Cyberpunk street handles — reuse punk name style
const NAME_ADJ = [
  "Static", "Neon", "Chrome", "Broken", "Rust", "Hollow", "Silent", "Feral",
  "Toxic", "Vapor", "Cold", "Late", "Torn", "Wired", "Ashen", "Glass",
];
const NAME_NOUN = [
  "Wire", "Fang", "Ghost", "Halo", "Blade", "Fuse", "Vein", "Signal",
  "Circuit", "Bone", "Loop", "Grid", "Spark", "Node", "Ash", "Rat",
];
const NAME_SUFFIX = ["-9", "-X", "-06", "//", "_v2", ".exe", "//13", "", "", "", "", ""];

function rollNpcName() {
  const style = Math.random();
  if (style < 0.55) return `${pick(NAME_ADJ)} ${pick(NAME_NOUN)}${pick(NAME_SUFFIX)}`.trim();
  const solos = ["Nyx", "Hex", "Ripper", "Meridian", "Kilobyte", "Threnody", "Fever", "Solvent", "Halcyon", "Grifter", "Ozone", "Cinder", "Petal", "Voidkid", "Feedback", "Molotov", "Rebar"];
  return `${pick(solos)}${pick(NAME_SUFFIX)}`.trim();
}

export function rollNpc() {
  return {
    kind: "npc",
    name: rollNpcName(),
    type: pick(NPC_TYPES),
    look: pick(NPC_LOOKS),
    quirk: pick(NPC_QUIRKS),
    motive: pick(NPC_MOTIVES),
    want: pick(NPC_WANTS),
  };
}

export function formatNpcBlock(n) {
  return [
    `// NPC — ${n.name}`,
    `TYPE:   ${n.type}`,
    `LOOK:   ${n.look}`,
    `QUIRK:  ${n.quirk}`,
    `MOTIVE: ${n.motive}`,
    `WANTS:  ${n.want}`,
  ].join("\n");
}

// ============================================================
// FOE GENERATOR
// ============================================================

// Each foe template is a stat blueprint. Stats and HP roll on generate.
// prs/str/agi/tou/kno are dice notation for the STAT MOD (rolled 1× on gen).
export const FOE_TEMPLATES = [
  {
    name: "CORP GUARD",
    desc: "Suited, armoured, board-authorised to end you.",
    hp: "d8+4",
    armor: 4,
    morale: 9,
    stats: { prs: "d3-1", str: "d4", agi: "d3", tou: "d4", kno: "d3-1" },
    weapons: [
      { name: "Assault Rifle", damage: "d10a" },
      { name: "Combat Baton", damage: "d6" },
    ],
    specials: [
      "Calls corp backup on the 2nd round.",
      "Bodycam broadcasts your face to HQ.",
      "Refuses to die unarmed; last stand attack ignores 1 armour.",
    ],
  },
  {
    name: "GANGER",
    desc: "All jaw, no plan. Colours matter more than survival.",
    hp: "d6+1",
    armor: 2,
    morale: 6,
    stats: { prs: "d3", str: "d4", agi: "d4", tou: "d3", kno: "d2-1" },
    weapons: [
      { name: "Baseball Bat", damage: "d6" },
      { name: "Zip-gun", damage: "d6" },
      { name: "Broken Bottle", damage: "d4" },
    ],
    specials: [
      "Screams before charging; DR12 Presence or DIS on your first shot.",
      "Fights harder as HP drops — +1 damage below half HP.",
      "Steals one item off your body if it lands the killing blow.",
    ],
  },
  {
    name: "MUTANT",
    desc: "The city grew this thing. It grew back at the city.",
    hp: "d10+2",
    armor: 3,
    morale: 10,
    stats: { prs: "d2-1", str: "d6+1", agi: "d3", tou: "d6", kno: "d2-1" },
    weapons: [
      { name: "Overgrown Claws", damage: "d6+1" },
      { name: "Bite (venomous)", damage: "d4+poison" },
    ],
    specials: [
      "Regenerates 1 HP at the start of its turn.",
      "Sprays acidic bile in a 3m cone on death — DR14 Agility or d6.",
      "Infects on a bite — DR14 Toughness in d4 hours or gain 1 GLITCH.",
    ],
  },
  {
    name: "KILL-DRONE",
    desc: "Serial-numbered angel. Auto-targeting. No conscience.",
    hp: "d6+2",
    armor: 6,
    morale: 12,
    stats: { prs: "d1", str: "d4", agi: "d6", tou: "d4", kno: "d6" },
    weapons: [
      { name: "Auto-rifle", damage: "d10a" },
      { name: "Micro-missile", damage: "d10" },
    ],
    specials: [
      "Thermal vision — ignores concealment and smoke.",
      "Self-destructs on 0 HP; DR14 Agility within 3m or take d8.",
      "Uploads footage every round; DR10 Knowledge (HACK) to blind it.",
    ],
  },
  {
    name: "CULTIST",
    desc: "Praying to a signal that doesn't answer.",
    hp: "d6",
    armor: 2,
    morale: 12,
    stats: { prs: "d4", str: "d3", agi: "d3", tou: "d3", kno: "d4" },
    weapons: [
      { name: "Ceremonial Knife", damage: "d4" },
      { name: "Grafted Shockprod", damage: "d6" },
    ],
    specials: [
      "Chanting: allies within 6m take +1 morale.",
      "Explodes with black light on death — DR12 Toughness or blind d4 rounds.",
      "Cannot be intimidated; ignore Presence checks.",
    ],
  },
  {
    name: "STRAY SYNTH",
    desc: "Discarded chassis. Owner-locked. Definitely still owned.",
    hp: "d8",
    armor: 4,
    morale: 11,
    stats: { prs: "d2", str: "d6", agi: "d4", tou: "d6", kno: "d3" },
    weapons: [
      { name: "Servo Fist", damage: "d8" },
      { name: "Arc Welder", damage: "d6" },
    ],
    specials: [
      "Immune to poison, gas and psychic feedback.",
      "Cannot bleed — armour is halved but wounds don't stack.",
      "Freezes 1 round if you speak its serial number aloud.",
    ],
  },
  {
    name: "WIREHEAD JUNKIE",
    desc: "Head plugged into a socket in the wall. Twitching.",
    hp: "d4",
    armor: 0,
    morale: 4,
    stats: { prs: "d1", str: "d3-1", agi: "d4+1", tou: "d2-1", kno: "d6" },
    weapons: [
      { name: "Rusted Shiv", damage: "d4" },
      { name: "Neural Jolt (touch)", damage: "d6 psychic" },
    ],
    specials: [
      "One free reroll per scene, but takes d4 self-damage using it.",
      "Cannot fight for more than 3 rounds before collapsing.",
      "Screams a corp password if grabbed and shaken.",
    ],
  },
  {
    name: "CHROME BUTCHER",
    desc: "Freelance surgeon. Cleaver in each hand. Smiling.",
    hp: "d10",
    armor: 4,
    morale: 8,
    stats: { prs: "d3", str: "d6", agi: "d4", tou: "d4", kno: "d3" },
    weapons: [
      { name: "Bone Cleaver (×2)", damage: "d8" },
      { name: "Trepanning Drill", damage: "d6+ignore armour" },
    ],
    specials: [
      "On a killing blow, harvests one cybertech from your corpse.",
      "Sells your organs to a nearby corp in the next scene.",
      "Immune to bleed damage — his own blood is orange.",
    ],
  },
  {
    name: "FERAL DOG",
    desc: "Eight legs. Four eyes. Understands orders in three languages.",
    hp: "d6",
    armor: 2,
    morale: 7,
    stats: { prs: "d1", str: "d3", agi: "d6", tou: "d3", kno: "d1" },
    weapons: [
      { name: "Bite", damage: "d6" },
      { name: "Pounce (charge)", damage: "d4+knockdown" },
    ],
    specials: [
      "Attacks in packs of d4; each adds +1 to damage on the same target.",
      "Cannot be outrun in a straight line.",
      "Whimpers when its pack-leader dies; DR8 Presence or flees.",
    ],
  },
  {
    name: "NANO-CULT PRIEST",
    desc: "Whispers to grey rain. The rain answers.",
    hp: "d8",
    armor: 2,
    morale: 12,
    stats: { prs: "d6", str: "d2", agi: "d3", tou: "d3", kno: "d6" },
    weapons: [
      { name: "Nano Blast", damage: "d8" },
      { name: "Consecrated Blade", damage: "d6" },
    ],
    specials: [
      "Casts a random NANO POWER at start of combat (roll on your NANO table).",
      "Curse: if slain, the killer gains 1 GLITCH and 1 INFESTATION.",
      "Cannot be killed by fire — heals from it (d4).",
    ],
  },
  {
    name: "BOUNTY HUNTER",
    desc: "Contract on you. Personally offended it hasn't ended yet.",
    hp: "d8+3",
    armor: 3,
    morale: 9,
    stats: { prs: "d3", str: "d4", agi: "d6", tou: "d4", kno: "d4" },
    weapons: [
      { name: "Hunting Revolver", damage: "d10" },
      { name: "Monowire Garrote", damage: "d6+bleed" },
    ],
    specials: [
      "Rolls with ADV on the first shot each combat.",
      "Non-lethal: prefers cuffs, will fight to knock you out first.",
      "Never chases past a district line.",
    ],
  },
  {
    name: "SEWER PILGRIM",
    desc: "Half-blind. Half-mad. Fully committed.",
    hp: "d6+1",
    armor: 2,
    morale: 10,
    stats: { prs: "d2", str: "d4", agi: "d3", tou: "d4", kno: "d2" },
    weapons: [
      { name: "Rusted Rebar", damage: "d6" },
      { name: "Toxic Spit", damage: "d4+d4 poison" },
    ],
    specials: [
      "Immune to disease and radiation.",
      "Knows one shortcut through the district. Won't share.",
      "Buries the dead — will pause combat to drag a corpse away.",
    ],
  },
  // ============================================================
  // CORE RULEBOOK FOES — stats & names from the CY_BORG bestiary,
  // notes written in-house. Marked `book: true` so the UI can flag them.
  // ============================================================
  {
    name: "NANOGOON",
    book: true,
    desc: "Cheap muscle wired on synth-nano and dumb loyalty.",
    hp: "4",
    armor: 0,
    morale: 7,
    stats: { prs: "d2-1", str: "d4", agi: "d3", tou: "d3", kno: "d2-1" },
    weapons: [
      { name: "9mm Pistol", damage: "d6a" },
    ],
    specials: [
      "Cheap goon — first HP hit past armour also causes a fumble stagger (drop the pistol).",
    ],
  },
  {
    name: "SECOP (TASER)",
    book: true,
    desc: "Standard corp security shift, standard corp playbook.",
    hp: "6",
    armor: 2,
    morale: 8,
    stats: { prs: "d3", str: "d4", agi: "d3", tou: "d4", kno: "d3" },
    weapons: [
      { name: "Taser", damage: "d2, DR12 Toughness or fall" },
      { name: "Baton", damage: "d4" },
    ],
    specials: [
      "Called shift-partner arrives in d4 rounds.",
      "Non-lethal by default; will cuff a downed target.",
    ],
  },
  {
    name: "SECOP (SHOCKSTICK)",
    book: true,
    desc: "The one they send when the taser wasn't enough.",
    hp: "8",
    armor: 2,
    morale: 7,
    stats: { prs: "d3", str: "d4", agi: "d3", tou: "d4", kno: "d3" },
    weapons: [
      { name: "Shockstick", damage: "d4, DR12 Toughness or fall" },
    ],
    specials: [
      "On a successful hit, target must DR12 Toughness or lose next turn.",
    ],
  },
  {
    name: "CRAWLER",
    book: true,
    desc: "Four-legged autonomous kill-frame. Patrols on schedule.",
    hp: "10",
    armor: 4,
    morale: 12,
    stats: { prs: "d1", str: "d4", agi: "d4", tou: "d4", kno: "d4" },
    weapons: [
      { name: "Mounted Assault Rifle", damage: "d8a" },
    ],
    specials: [
      "Thermal sight — ignores concealment and smoke.",
      "Uploads footage every round; DR12 KNO (HACK) to jam.",
    ],
  },
  {
    name: "FLYER",
    book: true,
    desc: "Palm-sized surveillance drone with a bite.",
    hp: "8",
    armor: 0,
    morale: 12,
    stats: { prs: "d1", str: "d2", agi: "d6", tou: "d2", kno: "d4" },
    weapons: [
      { name: "Small SMG", damage: "d4a" },
    ],
    specials: [
      "Hovers — melee attackers roll with DIS unless they can reach up.",
      "Screams a corp signal when destroyed; d4 more inbound in 2 rounds.",
    ],
  },
  {
    name: "SKULKER",
    book: true,
    desc: "Matte-black assassination frame. Silent until it isn't.",
    hp: "14",
    armor: 4,
    morale: 12,
    stats: { prs: "d1", str: "d4", agi: "d6", tou: "d4", kno: "d4" },
    weapons: [
      { name: "Monoblade", damage: "d6" },
      { name: "Silenced SMG", damage: "d4a" },
    ],
    specials: [
      "Cloaked — first attack in a scene is ADV.",
      "Cannot be spotted by sound; only motion or thermal.",
    ],
  },
  {
    name: "VINDICATOR",
    book: true,
    desc: "Two-metre corporate warcrime on treads.",
    hp: "18",
    armor: 6,
    morale: 12,
    stats: { prs: "d1", str: "d6", agi: "d3", tou: "d6", kno: "d4" },
    weapons: [
      { name: "Assault Rifle", damage: "d8a" },
      { name: "Grenade Launcher", damage: "d6 to d3 targets" },
    ],
    specials: [
      "Made for combat — armour cannot be bypassed by mundane weapons under d6 damage.",
      "Reloads once per encounter, taking a full round.",
    ],
  },
  {
    name: "DOPPEL",
    book: true,
    desc: "Wears a synth-skin of someone you know. Smiles wrong.",
    hp: "12",
    armor: 2,
    morale: 10,
    stats: { prs: "d4", str: "d4", agi: "d4", tou: "d4", kno: "d3" },
    weapons: [
      { name: "Hidden Vibro-blade", damage: "d8" },
    ],
    specials: [
      "Perfect mimic — DR14 KNO to spot the mask before it strikes.",
      "First hit each combat is ADV (mask surprise).",
    ],
  },
  {
    name: "BLIGHTHUSK",
    book: true,
    desc: "A person the nano hollowed out and re-filled with hunger.",
    hp: "5",
    armor: 2,
    morale: 6,
    stats: { prs: "d1", str: "d3", agi: "d4", tou: "d3", kno: "d1" },
    weapons: [
      { name: "Corrupted Claws", damage: "d4 + infestation" },
    ],
    specials: [
      "On any hit, the target rolls a d20 INFESTATION check (DR14 TOU or gain one).",
      "Regenerates 1 HP per round while under a corp-lit sky.",
    ],
  },
  {
    name: "HEADHUNTER",
    book: true,
    desc: "A jarred cortex on servo-legs. Freezes what it sees.",
    hp: "3",
    armor: 0,
    morale: 5,
    stats: { prs: "d3", str: "d1", agi: "d3", tou: "d1", kno: "d6" },
    weapons: [
      { name: "Ocular Ray", damage: "d4a + freezing gaze" },
    ],
    specials: [
      "Freezing gaze: hit target must DR12 Toughness or lose next action.",
      "Fragile — one solid hit past armour usually ends it.",
    ],
  },
  {
    name: "WHITETAIL",
    book: true,
    desc: "A ghost-cat that phases in when the walls thin.",
    hp: "4",
    armor: 3,
    morale: 5,
    stats: { prs: "d2", str: "d2", agi: "d6", tou: "d2", kno: "d2" },
    weapons: [
      { name: "Phase Bite", damage: "d3 + phase venom" },
    ],
    specials: [
      "Spectral — half of physical damage phases through it.",
      "Phase venom: DR14 Toughness or take d4 poison each round for d4 rounds.",
    ],
  },
  {
    name: "BLOATED",
    book: true,
    desc: "A ballooned corpse that keeps walking.",
    hp: "18",
    armor: 0,
    morale: 12,
    stats: { prs: "d1", str: "d6", agi: "d1", tou: "d6", kno: "d1" },
    weapons: [
      { name: "Rotting Slam", damage: "d4" },
    ],
    specials: [
      "Bursts on death — DR14 Agility within 3m or take d6 rot damage.",
      "Ignores morale and pain; will never flee.",
    ],
  },
  {
    name: "MILCORP GUNSHIP",
    book: true,
    desc: "The last thing you see when a district gets condemned.",
    hp: "45",
    armor: 6,
    morale: 6,
    stats: { prs: "d1", str: "d6", agi: "d3", tou: "d6", kno: "d4" },
    weapons: [
      { name: "Gatling Guns", damage: "d10a" },
      { name: "Rocket Pod", damage: "d12 to d4 targets" },
    ],
    specials: [
      "Flight — melee cannot reach unless it lands.",
      "Corp-eye targeting — anti-hack countermeasures give DIS to any KNO/hack attempts.",
      "On 0 HP, crashes: DR14 Agility within 6m or take d10.",
    ],
  },
];

export const FOE_ADJECTIVES = [
  "SCARRED", "CHROMED", "STARVING", "BLOODY",
  "GLITCHED", "BURNING", "WIRED", "HOLLOW",
  "GRAFTED", "STIMMED",
];

// mod values are ±. Rolled once at generation.
function rollStatMod(dieStr) {
  // supports "d4", "d6+1", "d3-1", "d1" (fixed 0), "0"
  if (!dieStr || dieStr === "0") return 0;
  if (dieStr === "d1") return 0;
  const raw = parseDie(dieStr);
  // stat mods in CY_BORG are -3..+6, clamp
  return Math.max(-3, Math.min(6, raw));
}

export function rollFoe(templateIndex = null) {
  const tmpl =
    typeof templateIndex === "number" && templateIndex >= 0 && templateIndex < FOE_TEMPLATES.length
      ? FOE_TEMPLATES[templateIndex]
      : pick(FOE_TEMPLATES);
  return rollFoeFromTemplate(tmpl);
}

// Roll a single foe instance from any template shape (built-in OR custom).
export function rollFoeFromTemplate(tmpl) {
  const adj = pick(FOE_ADJECTIVES);
  const hp = parseDie(tmpl.hp);
  // armor is now a fixed die-size integer (0 = no armor, 2 = d2, 4 = d4, etc.)
  const armor = typeof tmpl.armor === "number" ? tmpl.armor : 0;
  const stats = {
    presence: rollStatMod(tmpl.stats.prs),
    strength: rollStatMod(tmpl.stats.str),
    agility: rollStatMod(tmpl.stats.agi),
    toughness: rollStatMod(tmpl.stats.tou),
    knowledge: rollStatMod(tmpl.stats.kno),
  };
  return {
    kind: "foe",
    name: `${adj} ${tmpl.name}`,
    type: tmpl.name,
    desc: tmpl.desc,
    book: !!tmpl.book,
    hp,
    hp_max: hp,
    hp_die: tmpl.hp,
    armor,
    armor_die: armor > 0 ? `d${armor}` : "0",
    morale: tmpl.morale,
    weapons: tmpl.weapons,       // all weapons for GM choice
    specials: tmpl.specials,     // all specials
    // convenience "one pick" for LIGHT mode:
    weapon: pick(tmpl.weapons),
    special: pick(tmpl.specials),
    stats,
  };
}

const statLine = (s) => {
  const p = (k, v) => `${k}:${(v >= 0 ? "+" : "")}${v}`;
  return `${p("PRS", s.presence)}  ${p("STR", s.strength)}  ${p("AGI", s.agility)}  ${p("TOU", s.toughness)}  ${p("KNO", s.knowledge)}`;
};

export function formatFoeBlockLight(f) {
  return [
    `// FOE — ${f.name}`,
    f.desc,
    `HP ${f.hp}   ARMOR ${f.armor > 0 ? "−d" + f.armor : "0"}   MORALE ${f.morale}`,
    `ATTACK: ${f.weapon.name} (${f.weapon.damage})`,
    `SPECIAL: ${f.special}`,
  ].join("\n");
}

export function formatFoeBlockFull(f) {
  const weaponLines = f.weapons.map((w) => `  · ${w.name} (${w.damage})`).join("\n");
  const specialLines = f.specials.map((s) => `  · ${s}`).join("\n");
  return [
    `// FOE — ${f.name}`,
    f.desc,
    `HP ${f.hp}/${f.hp_max}  (${f.hp_die})   ARMOR ${f.armor > 0 ? "−d" + f.armor : "0"}  (${f.armor_die})   MORALE ${f.morale}`,
    `STATS: ${statLine(f.stats)}`,
    `WEAPONS:`,
    weaponLines,
    `SPECIALS:`,
    specialLines,
  ].join("\n");
}
